import React, { useState, useEffect } from 'react';
import { Brain, TrendingUp, Target, Lightbulb, AlertTriangle, CheckCircle, BarChart3, Activity } from 'lucide-react';
import { ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, AreaChart, Area } from 'recharts';

interface AIAnalyticsRecommendationsProps {
  projectId: number;
  processSteps: any[];
  riskAssessments?: any[];
  existingData?: any;
  viewMode?: string;
  onAnalysisComplete?: (data: any) => void;
  viewType?: 'analytics' | 'recommendations';
}

interface AIRecommendation {
  id: string;
  category: 'immediate' | 'short_term' | 'strategic';
  priority: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  impact_score: number;
  effort_required: 'low' | 'medium' | 'high';
  estimated_cost: string;
  timeline: string;
  success_probability: number;
  roi_projection: number;
  regulatory_compliance: string[];
  implementation_steps: string[];
  success_metrics: string[];
  ai_rationale: string;
}

interface PredictiveInsights {
  risk_trend_analysis: {
    direction: 'improving' | 'stable' | 'deteriorating';
    velocity: number;
    confidence: number;
    key_drivers: string[];
  };
  optimization_opportunities: {
    process_consolidation: number;
    automation_potential: number;
    control_efficiency: number;
    cost_reduction: number;
  };
  benchmark_analysis: {
    industry_position: number;
    peer_comparison: {
      percentile: number;
      gap_analysis: string[];
      improvement_areas: string[];
    };
  };
}

const AIAnalyticsRecommendations: React.FC<AIAnalyticsRecommendationsProps> = ({
  projectId,
  processSteps,
  riskAssessments = [],
  existingData = {},
  viewMode = 'Consultant',
  onAnalysisComplete = () => {},
  viewType = 'analytics'
}) => {
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState<'analytics' | 'recommendations'>(viewType);
  const [aiRecommendations, setAiRecommendations] = useState<AIRecommendation[]>(existingData?.recommendations || []);
  const [predictiveInsights, setPredictiveInsights] = useState<PredictiveInsights | null>(existingData?.insights || null);
  const [selectedRecommendation, setSelectedRecommendation] = useState<AIRecommendation | null>(null);
  const [trendData, setTrendData] = useState<any[]>(existingData?.trend_data || []);

  const performAIAnalysis = async () => {
    setLoading(true);
    try {
      // Generate mock AI analytics and recommendations
      await new Promise(resolve => setTimeout(resolve, 2000)); // Realistic delay

      // Mock predictive insights
      const mockInsights: PredictiveInsights = {
        risk_trend_analysis: {
          direction: 'improving',
          velocity: 0.15,
          confidence: 0.87,
          key_drivers: ['Enhanced control implementation', 'Process automation', 'Regulatory compliance improvements']
        },
        optimization_opportunities: {
          process_consolidation: 0.25,
          automation_potential: 0.65,
          control_efficiency: 0.40,
          cost_reduction: 0.18
        },
        benchmark_analysis: {
          industry_position: 75,
          peer_comparison: {
            percentile: 72,
            gap_analysis: ['Process automation lagging', 'Control effectiveness above average'],
            improvement_areas: ['Real-time monitoring', 'Predictive analytics', 'Process optimization']
          }
        }
      };

      setPredictiveInsights(mockInsights);

      // Mock trend data for charts
      const mockTrendData = [
        { month: 'Jan', risk_score: 15.2, control_effectiveness: 3.8, compliance: 89 },
        { month: 'Feb', risk_score: 14.8, control_effectiveness: 4.0, compliance: 91 },
        { month: 'Mar', risk_score: 14.1, control_effectiveness: 4.1, compliance: 93 },
        { month: 'Apr', risk_score: 13.5, control_effectiveness: 4.3, compliance: 94 },
        { month: 'May', risk_score: 12.9, control_effectiveness: 4.4, compliance: 96 },
        { month: 'Jun', risk_score: 12.2, control_effectiveness: 4.6, compliance: 97 }
      ];

      setTrendData(mockTrendData);

      // Mock AI recommendations
      const mockRecommendations: AIRecommendation[] = [
        {
          id: 'rec-1',
          category: 'immediate',
          priority: 'critical',
          title: 'Implement Real-time Payment Monitoring',
          description: 'Deploy AI-powered monitoring system for financial transactions to detect anomalies and prevent fraud in real-time.',
          impact_score: 9.2,
          effort_required: 'high',
          estimated_cost: '$75k - $120k',
          timeline: '8-12 weeks',
          success_probability: 0.92,
          roi_projection: 285,
          regulatory_compliance: ['Basel III', 'SOX Section 302'],
          implementation_steps: [
            'Conduct vendor evaluation and selection',
            'Design integration architecture',
            'Develop and test monitoring rules',
            'Deploy system with pilot group',
            'Full rollout with training'
          ],
          success_metrics: [
            'Fraud detection rate > 98%',
            'False positive rate < 2%',
            'Response time < 30 seconds'
          ],
          ai_rationale: 'High-frequency payment processes show elevated risk patterns. Real-time monitoring will reduce financial exposure by 85% and improve regulatory compliance.'
        },
        {
          id: 'rec-2',
          category: 'short_term',
          priority: 'high',
          title: 'Automate Approval Workflows',
          description: 'Implement intelligent approval routing and automated decision-making for routine transactions.',
          impact_score: 7.8,
          effort_required: 'medium',
          estimated_cost: '$35k - $60k',
          timeline: '6-8 weeks',
          success_probability: 0.88,
          roi_projection: 195,
          regulatory_compliance: ['COSO Control Environment', 'SOX Section 404'],
          implementation_steps: [
            'Map current approval processes',
            'Design automated workflow engine',
            'Configure business rules',
            'Test with sample transactions',
            'Deploy and monitor'
          ],
          success_metrics: [
            'Process time reduction > 70%',
            'Approval accuracy > 99%',
            'User satisfaction > 85%'
          ],
          ai_rationale: 'Manual approval processes create bottlenecks and inconsistency. Automation will improve efficiency and reduce human error by 60%.'
        },
        {
          id: 'rec-3',
          category: 'strategic',
          priority: 'medium',
          title: 'Implement Predictive Risk Analytics',
          description: 'Deploy machine learning models to predict and prevent risk events before they occur.',
          impact_score: 8.5,
          effort_required: 'high',
          estimated_cost: '$120k - $200k',
          timeline: '16-24 weeks',
          success_probability: 0.75,
          roi_projection: 340,
          regulatory_compliance: ['ISO 31000', 'NIST Framework'],
          implementation_steps: [
            'Collect and prepare historical data',
            'Develop predictive models',
            'Build analytics dashboard',
            'Pilot with select processes',
            'Scale across organization'
          ],
          success_metrics: [
            'Risk prediction accuracy > 85%',
            'Early warning time > 48 hours',
            'Risk mitigation effectiveness > 75%'
          ],
          ai_rationale: 'Pattern analysis shows predictable risk indicators. ML models can provide 48-72 hour early warnings for 78% of risk events.'
        }
      ];

      setAiRecommendations(mockRecommendations);

      // Auto-select first recommendation
      if (mockRecommendations.length > 0) {
        setSelectedRecommendation(mockRecommendations[0]);
      }

      // Persist the results
      const analysisData = {
        insights: mockInsights,
        trend_data: mockTrendData,
        recommendations: mockRecommendations
      };
      onAnalysisComplete(analysisData);

    } catch (error) {
      console.error('AI analytics failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sync with existing data from parent
  useEffect(() => {
    if (existingData?.recommendations?.length > 0) {
      setAiRecommendations(existingData.recommendations);
      setPredictiveInsights(existingData.insights);
      setTrendData(existingData.trend_data);
      // Auto-select first recommendation
      if (!selectedRecommendation && existingData.recommendations.length > 0) {
        setSelectedRecommendation(existingData.recommendations[0]);
      }
    }
  }, [existingData, selectedRecommendation]);

  // Auto-select first recommendation when recommendations are loaded
  useEffect(() => {
    if (aiRecommendations.length > 0 && !selectedRecommendation) {
      setSelectedRecommendation(aiRecommendations[0]);
    }
  }, [aiRecommendations, selectedRecommendation]);

  // Auto-load analysis when process steps are available (only if no existing data)
  useEffect(() => {
    if (processSteps.length > 0 && aiRecommendations.length === 0 && !existingData?.recommendations?.length && !loading) {
      performAIAnalysis();
    }
  }, [processSteps, projectId, existingData, aiRecommendations.length, loading, performAIAnalysis]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'immediate': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'short_term': return <TrendingUp className="w-5 h-5 text-yellow-500" />;
      case 'strategic': return <Target className="w-5 h-5 text-blue-500" />;
      default: return <Lightbulb className="w-5 h-5 text-gray-500" />;
    }
  };

  const getEffortColor = (effort: string) => {
    switch (effort) {
      case 'low': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'high': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Toggle */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">AI Analytics & Strategic Recommendations</h3>
              <p className="text-sm text-gray-600">Predictive insights and intelligent optimization strategies</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="bg-white rounded-lg p-1 border border-gray-200">
              <button
                onClick={() => setActiveView('analytics')}
                className={`px-4 py-2 rounded font-medium text-sm transition-colors ${
                  activeView === 'analytics' 
                    ? 'bg-indigo-600 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Analytics
              </button>
              <button
                onClick={() => setActiveView('recommendations')}
                className={`px-4 py-2 rounded font-medium text-sm transition-colors ${
                  activeView === 'recommendations' 
                    ? 'bg-indigo-600 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Recommendations
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics View */}
      {activeView === 'analytics' && predictiveInsights && (
        <div className="space-y-6">
          {/* Key Insights Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-8 h-8 text-blue-500" />
                <span className="text-xs font-medium text-blue-600 uppercase">Risk Trend</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 capitalize">
                {predictiveInsights.risk_trend_analysis.direction}
              </div>
              <p className="text-sm text-gray-600">
                {predictiveInsights.risk_trend_analysis.confidence}% confidence
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <BarChart3 className="w-8 h-8 text-green-500" />
                <span className="text-xs font-medium text-green-600 uppercase">Automation</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {predictiveInsights.optimization_opportunities.automation_potential}%
              </div>
              <p className="text-sm text-gray-600">Potential improvement</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Target className="w-8 h-8 text-purple-500" />
                <span className="text-xs font-medium text-purple-600 uppercase">Industry Rank</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {predictiveInsights.benchmark_analysis.peer_comparison.percentile}%
              </div>
              <p className="text-sm text-gray-600">vs. industry peers</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Activity className="w-8 h-8 text-orange-500" />
                <span className="text-xs font-medium text-orange-600 uppercase">Cost Reduction</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {predictiveInsights.optimization_opportunities.cost_reduction}%
              </div>
              <p className="text-sm text-gray-600">Potential savings</p>
            </div>
          </div>

          {/* Trend Analysis Chart */}
          {trendData.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Risk Trend Forecast</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="predicted_risk"
                      stroke="#8b5cf6"
                      fill="#8b5cf6"
                      fillOpacity={0.1}
                      name="Predicted Risk"
                    />
                    <Area
                      type="monotone"
                      dataKey="current_risk"
                      stroke="#10b981"
                      fill="#10b981"
                      fillOpacity={0.1}
                      name="Current Risk"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Optimization Opportunities */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Optimization Opportunities</h4>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Process Consolidation</span>
                    <span className="text-sm font-bold text-indigo-600">
                      {predictiveInsights.optimization_opportunities.process_consolidation}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-indigo-600 h-2 rounded-full" 
                      style={{ width: `${predictiveInsights.optimization_opportunities.process_consolidation}%` }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Control Efficiency</span>
                    <span className="text-sm font-bold text-green-600">
                      {predictiveInsights.optimization_opportunities.control_efficiency}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${predictiveInsights.optimization_opportunities.control_efficiency}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Benchmark Analysis</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Industry Position:</span>
                  <span className="font-medium text-gray-900">
                    {predictiveInsights.benchmark_analysis.industry_position}/100
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600 block mb-2">Key Improvement Areas:</span>
                  <div className="space-y-1">
                    {predictiveInsights.benchmark_analysis.peer_comparison.improvement_areas.map((area, idx) => (
                      <div key={idx} className="bg-yellow-50 text-yellow-800 px-2 py-1 rounded text-xs">
                        {area}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recommendations View */}
      {activeView === 'recommendations' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recommendations List */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900">AI Strategic Recommendations</h4>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {aiRecommendations.map((rec) => (
                <div
                  key={rec.id}
                  onClick={() => setSelectedRecommendation(rec)}
                  className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                    selectedRecommendation?.id === rec.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {getCategoryIcon(rec.category)}
                      <h5 className="font-medium text-gray-900">{rec.title}</h5>
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityColor(rec.priority)}`}>
                      {rec.priority.toUpperCase()}
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">{rec.description}</p>
                  
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">Impact:</span>
                      <span className="ml-1 font-medium text-green-600">{rec.impact_score}/10</span>
                    </div>
                    <div>
                      <span className="text-gray-500">ROI:</span>
                      <span className="ml-1 font-medium text-blue-600">{rec.roi_projection}%</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Success:</span>
                      <span className="ml-1 font-medium text-purple-600">{rec.success_probability}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendation Details */}
          {selectedRecommendation && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-2 mb-4">
                {getCategoryIcon(selectedRecommendation.category)}
                <h4 className="text-lg font-semibold text-gray-900">{selectedRecommendation.title}</h4>
                <div className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityColor(selectedRecommendation.priority)}`}>
                  {selectedRecommendation.priority.toUpperCase()}
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">{selectedRecommendation.description}</p>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <span className="text-sm font-medium text-gray-700">Estimated Cost:</span>
                  <div className="text-lg font-semibold text-green-600">{selectedRecommendation.estimated_cost}</div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Timeline:</span>
                  <div className="text-lg font-semibold text-blue-600">{selectedRecommendation.timeline}</div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Effort Required:</span>
                  <div className={`text-lg font-semibold ${getEffortColor(selectedRecommendation.effort_required)}`}>
                    {selectedRecommendation.effort_required}
                  </div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">ROI Projection:</span>
                  <div className="text-lg font-semibold text-purple-600">{selectedRecommendation.roi_projection}%</div>
                </div>
              </div>

              {/* AI Rationale */}
              <div className="mb-4">
                <h6 className="font-medium text-gray-700 mb-2 flex items-center">
                  <Brain className="w-4 h-4 mr-2 text-purple-500" />
                  AI Rationale
                </h6>
                <div className="bg-purple-50 border border-purple-200 rounded p-3 text-sm text-purple-800">
                  {selectedRecommendation.ai_rationale}
                </div>
              </div>

              {/* Implementation Steps */}
              <div className="mb-4">
                <h6 className="font-medium text-gray-700 mb-2">Implementation Steps</h6>
                <ol className="space-y-2">
                  {selectedRecommendation.implementation_steps.map((step, idx) => (
                    <li key={idx} className="text-sm text-gray-700 flex items-start">
                      <span className="w-5 h-5 bg-indigo-100 text-indigo-600 rounded-full text-xs flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                        {idx + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>

              {/* Success Metrics */}
              <div className="mb-4">
                <h6 className="font-medium text-gray-700 mb-2">Success Metrics</h6>
                <div className="space-y-1">
                  {selectedRecommendation.success_metrics.map((metric, idx) => (
                    <div key={idx} className="text-sm text-gray-700 flex items-center">
                      <CheckCircle className="w-3 h-3 text-green-500 mr-2 flex-shrink-0" />
                      {metric}
                    </div>
                  ))}
                </div>
              </div>

              {/* Regulatory Compliance */}
              {selectedRecommendation.regulatory_compliance.length > 0 && (
                <div>
                  <h6 className="font-medium text-gray-700 mb-2">Regulatory Compliance</h6>
                  <div className="flex flex-wrap gap-2">
                    {selectedRecommendation.regulatory_compliance.map((compliance, idx) => (
                      <span key={idx} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs border border-blue-200">
                        {compliance}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="text-center py-12">
          <Brain className="w-8 h-8 animate-pulse text-indigo-500 mx-auto mb-4" />
          <p className="text-gray-600">AI is analyzing and generating recommendations...</p>
        </div>
      )}
    </div>
  );
};

export default AIAnalyticsRecommendations;